/**
 * Get user avatar URL
 * Returns a static avatar image based on user ID (deterministic)
 * Falls back to first letter of name if no avatar available
 * 
 * @param {Object} user - User object with id, firstName, lastName
 * @param {string} avatarUrl - Optional custom avatar URL
 * @returns {string} Avatar URL or null for fallback
 */
export const getUserAvatar = (user, avatarUrl = null) => {
    // If custom avatar URL is provided, use it
    if (avatarUrl) {
        return avatarUrl;
    }

    // If user has avatar_url, use it
    if (user?.avatar_url) {
        return user.avatar_url;
    }

    // Select static avatar based on user ID (deterministic)
    // This ensures same user always gets same avatar
    if (user?.id) {
        const avatarOptions = [
            '/images/avatars/avatar-1.png',
            '/images/avatars/avatar-2.png',
            '/images/avatars/avatar-3.png',
            '/images/avatars/avatar-4.png',
            '/images/avatars/avatar-5.png',
        ];

        // Use last character of ID to select avatar (0-9, a-z)
        const lastChar = user.id.slice(-1);
        const index = parseInt(lastChar, 36) % avatarOptions.length;
        
        return avatarOptions[index];
    }

    // No avatar available, return null to show initials
    return null;
};

/**
 * Get user initials for avatar fallback
 * @param {Object} user - User object with firstName, lastName
 * @returns {string} Initials (e.g., "UA" for "User ABC12")
 */
export const getUserInitials = (user) => {
    if (!user) return '';
    
    const firstName = user.firstName || user.first_name || '';
    const lastName = user.lastName || user.last_name || '';
    
    const firstInitial = firstName.charAt(0).toUpperCase();
    const lastInitial = lastName.charAt(0).toUpperCase();
    
    return (firstInitial + lastInitial) || firstName.charAt(0).toUpperCase() || 'U';
};

